import { useEffect, useState } from "react";
import BackofficeLayout, { Card, Btn, fmtEUR } from "./BackofficeLayout";
import { api } from "../../lib/api";
import { toast, Toaster } from "sonner";
import { Plus, Trash2, Edit, Landmark } from "lucide-react";

const empty = {
  entity_id: "", name: "", iban: "", bank_name: "",
  account_type: "courant", opening_balance: 0, currency: "EUR",
  balance_date: new Date().toISOString().slice(0, 10),
};

export default function BankAccountsAdmin() {
  const [items, setItems] = useState([]);
  const [entities, setEntities] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [showForm, setShowForm] = useState(false);

  const load = () =>
    Promise.all([
      api.get("/backoffice/bank/accounts").then((r) => r.data),
      api.get("/backoffice/entities").then((r) => r.data),
    ]).then(([a, e]) => { setItems(a); setEntities(e); });
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      opening_balance: parseFloat(form.opening_balance) || 0,
      balance_date: form.balance_date || null,
    };
    try {
      if (editing) {
        await api.put(`/backoffice/bank/accounts/${editing}`, payload);
        toast.success("Compte mis à jour");
      } else {
        await api.post("/backoffice/bank/accounts", payload);
        toast.success("Compte créé");
      }
      setForm(empty); setEditing(null); setShowForm(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const startEdit = (a) => {
    setEditing(a.id);
    setForm({
      entity_id: a.entity_id, name: a.name,
      iban: a.iban || "", bank_name: a.bank_name || "",
      account_type: a.account_type, opening_balance: a.opening_balance,
      currency: a.currency || "EUR",
      balance_date: a.balance_date || new Date().toISOString().slice(0, 10),
    });
    setShowForm(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce compte ?")) return;
    try {
      await api.delete(`/backoffice/bank/accounts/${id}`);
      toast.success("Supprimé");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const openForm = () => {
    setEditing(null);
    setForm({ ...empty, entity_id: entities[0]?.id || "" });
    setShowForm(true);
  };

  return (
    <BackofficeLayout
      title="Comptes bancaires"
      subtitle="Rattachés aux entités. Le solde est calculé automatiquement à partir du solde d'ouverture + transactions."
      actions={<Btn onClick={openForm} data-testid="bof-account-new-btn"><Plus className="w-3.5 h-3.5" /> Nouveau compte</Btn>}
    >
      <Toaster theme="dark" position="top-right" />
      {showForm && (
        <Card className="mb-6" data-testid="bof-account-form">
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Entité">
              <select required value={form.entity_id} onChange={(e) => setForm({ ...form, entity_id: e.target.value })} className="input" data-testid="bof-account-entity">
                <option value="">— Choisir —</option>
                {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </Field>
            <Field label="Nom du compte">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Compte courant CA" className="input" data-testid="bof-account-name" />
            </Field>
            <Field label="Banque">
              <input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} placeholder="Crédit Agricole" className="input" data-testid="bof-account-bank" />
            </Field>
            <Field label="IBAN (optionnel)">
              <input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} className="input" data-testid="bof-account-iban" />
            </Field>
            <Field label="Type">
              <select value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value })} className="input" data-testid="bof-account-type">
                <option value="courant">Compte courant</option>
                <option value="epargne">Épargne</option>
                <option value="credit">Crédit</option>
                <option value="autre">Autre</option>
              </select>
            </Field>
            <Field label="Solde d'ouverture (€)">
              <input type="number" step="0.01" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} className="input" data-testid="bof-account-opening" />
            </Field>
            <Field label="Date de référence du solde">
              <input type="date" value={form.balance_date}
                     onChange={(e) => setForm({ ...form, balance_date: e.target.value })}
                     className="input" data-testid="bof-account-balance-date" />
              <div className="text-[10px] text-slate-500 mt-1">
                Date à laquelle le solde était à jour. Les loyers/prêts avec jour ≤ ce jour sont considérés déjà passés dans le mois courant.
              </div>
            </Field>
            <div className="md:col-span-2 flex gap-2">
              <Btn type="submit" data-testid="bof-account-save">{editing ? "Enregistrer" : "Créer"}</Btn>
              <Btn type="button" variant="ghost" onClick={() => { setShowForm(false); setEditing(null); }}>Annuler</Btn>
            </div>
          </form>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 min-h-[80px]" data-testid="bof-accounts-list">
        {items.length === 0 && (
          <div className="col-span-full text-center text-slate-500 py-8 border border-dashed border-slate-800 rounded-lg">
            Aucun compte bancaire. Cliquez sur &quot;Nouveau compte&quot; pour en ajouter.
          </div>
        )}
        {items.map((a) => (
          <Card key={a.id} data-testid={`bof-account-${a.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <Landmark className="w-5 h-5 text-amber-400" />
                <div>
                  <div className="font-bold text-white">{a.name}</div>
                  <div className="text-xs text-slate-500">{a.entity_name}</div>
                </div>
              </div>
              <div className="flex gap-1">
                <Btn variant="ghost" onClick={() => startEdit(a)} data-testid={`bof-account-edit-${a.id}`}>
                  <Edit className="w-3.5 h-3.5" />
                </Btn>
                <Btn variant="danger" onClick={() => remove(a.id)} data-testid={`bof-account-delete-${a.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Btn>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-slate-800">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Solde actuel</div>
              <div className={`text-2xl font-black mt-1 ${a.current_balance >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                {fmtEUR(a.current_balance)}
              </div>
              <div className="text-xs text-slate-500 mt-1">Ouverture {fmtEUR(a.opening_balance)}</div>
              {a.balance_date && (
                <div className="text-[10px] text-amber-400 mt-1" data-testid={`bof-account-balance-date-${a.id}`}>
                  Solde au {a.balance_date}
                </div>
              )}
            </div>
            {a.bank_name && <div className="text-xs text-slate-500 mt-3">{a.bank_name}</div>}
          </Card>
        ))}
      </div>

      <style>{`
        .input { background: rgba(15, 23, 42, 0.6); border: 1px solid rgb(51, 65, 85); color: white;
                 padding: 0.5rem 0.75rem; border-radius: 0.375rem; width: 100%; font-size: 0.875rem; }
        .input:focus { outline: 2px solid rgb(251, 191, 36); outline-offset: -1px; }
      `}</style>
    </BackofficeLayout>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1 block">{label}</span>
    {children}
  </label>
);
